import { ComponentType, lazy } from 'react';

const RELOAD_KEY = 'howio:chunk-reload-at';
const RELOAD_COOLDOWN_MS = 30_000;

/**
 * Chunk/module load failures as reported by Vite (`vite:preloadError`) and each browser's
 * native `import()`: Chrome "Failed to fetch dynamically imported module", Firefox "error
 * loading dynamically imported module", Safari "Importing a module script failed".
 */
export const isChunkLoadError = (error: unknown): boolean => {
  const { name, message = '' } = (error ?? {}) as { name?: string; message?: string };
  return (
    name === 'ChunkLoadError' ||
    /dynamically imported module|importing a module script failed|unable to preload css|loading (css )?chunk/i.test(
      message,
    )
  );
};

/**
 * A missing chunk usually means a deploy replaced the hashed assets under an open tab, so
 * retrying can't help — reload to pick up the new build. The cooldown stops a reload loop
 * when the asset really is gone; the caller then gets the error (and the error boundary).
 */
export const reloadForNewBuild = (): boolean => {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Storage blocked: reloading without a loop guard is riskier than surfacing the error.
    return false;
  }
  window.location.reload();
  return true;
};

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Chrome caches a failed module fetch, so re-running the same `import()` rejects again
 * without hitting the network. Its error message carries the chunk URL, so retry that
 * URL with a cache-busting query instead.
 */
const bustedImport = (error: unknown) => {
  const url = String((error as Error)?.message ?? '').match(/https?:\/\/\S+\.m?js/)?.[0];
  if (!url) return null;
  const busted = new URL(url);
  busted.searchParams.set('t', String(Date.now()));
  return import(/* @vite-ignore */ busted.href);
};

/**
 * Runs a dynamic import, retrying chunk load failures with backoff. If every attempt fails,
 * reloads the page once and leaves the returned promise pending, so Suspense keeps showing
 * its fallback until the page reloads.
 */
export const retryImport = async <T>(factory: () => Promise<T>, retries = 3, delayMs = 500): Promise<T> => {
  let lastError: unknown;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      if (attempt === 0) return await factory();
      return ((await bustedImport(lastError)) as T | null) ?? (await factory());
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;
      lastError = error;
      if (attempt < retries) await wait(delayMs * 2 ** attempt);
    }
  }

  if (reloadForNewBuild()) return new Promise<T>(() => {});
  throw lastError;
};

/** `React.lazy` with chunk-load retries. Use for every code-split component. */
export const lazyWithRetry = <T extends ComponentType<any>>(factory: () => Promise<{ default: T }>) =>
  lazy(() => retryImport(factory));
